// mapArea — "search this area" and draw-on-map (#3078). An area is a single
// ring of [lng, lat] points: the map's visible extent, or a polygon the user
// drew. Pure, so the membership rule is unit-tested without ArcGIS.

export type MapAreaKind = "extent" | "polygon";

export interface MapArea {
  kind: MapAreaKind;
  /** Closed or open ring of [lng, lat]; the closing edge is implied. */
  ring: [number, number][];
}

/** A rectangle as a ring, from geographic extent bounds. */
export function extentRing(xmin: number, ymin: number, xmax: number, ymax: number): [number, number][] {
  return [
    [xmin, ymin],
    [xmax, ymin],
    [xmax, ymax],
    [xmin, ymax],
  ];
}

/** Even-odd ray casting. Points exactly on an edge may fall either way. */
export function pointInRing(lng: number, lat: number, ring: [number, number][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    const crosses = (yi > lat) !== (yj > lat) && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
    if (crosses) inside = !inside;
  }
  return inside;
}

/** Rows whose coordinates fall inside the area. Rows without coordinates never do. */
export function filterToArea<T extends { longitude: number | null; latitude: number | null }>(
  rows: T[],
  area: MapArea
): T[] {
  if (area.ring.length < 3) return [];
  return rows.filter(
    (r) =>
      typeof r.longitude === "number" &&
      typeof r.latitude === "number" &&
      pointInRing(r.longitude, r.latitude, area.ring)
  );
}
